import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { authLogout, changePassword, getBotKnowledgePendingCount } from "@/lib/api";
import {
  LayoutDashboard, Users, Tag, Bike, MapPin, Megaphone,
  MessageSquare, Bot, Settings, LogOut, KeyRound, BookOpen,
  Building2, CalendarDays, IdCard, SlidersHorizontal, Zap, Activity, BarChart3, Radio, HelpCircle, AlertTriangle, Smartphone
, Gauge, Gift, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Panel", end: true },
  { to: "/analytics", icon: BarChart3, label: "Satis Analitigi" },
  { to: "/departments", icon: Building2, label: "Birimler" },
  { to: "/leads", icon: Users, label: "Leadler" },
  { to: "/customers", icon: IdCard, label: "Musteriler (CRM)" },
  { to: "/brands", icon: Tag, label: "Markalar" },
  { to: "/models", icon: Bike, label: "Modeller" },
  { to: "/branches", icon: MapPin, label: "Subeler" },
  { to: "/campaigns", icon: Megaphone, label: "Kampanyalar" },
  { to: "/appointments", icon: CalendarDays, label: "Randevular" },
  { to: "/conversations", icon: MessageSquare, label: "Konusmalar" },
  { to: "/chatbot", icon: Bot, label: "Chatbot Test" },
  { to: "/chatbot-content", icon: BookOpen, label: "Chatbot Icerik" },
  { to: "/bot-settings", icon: SlidersHorizontal, label: "Bot Ayarlari" },
  { to: "/pipeline-settings", icon: Zap, label: "Satis Akisi" },
  { to: "/pipeline-logs", icon: Activity, label: "Akis Takip" },
  { to: "/complaints", icon: AlertTriangle, label: "Sikayetler" },
  { to: "/nps", icon: Gauge, label: "NPS Memnuniyet" },
  { to: "/gifts", icon: Gift, label: "Hediye & Tesvik" },
  { to: "/scoreboard", icon: Trophy, label: "Danisman Puanlari" },
  { to: "/broadcast", icon: Radio, label: "Kampanya Gonder" },
  { to: "/settings", icon: Settings, label: "Ayarlar" },
  { to: "/help", icon: HelpCircle, label: "Yardim" },
  { to: "/m", icon: Smartphone, label: "Mobil Uygulama" },
];

export default function Sidebar({ user, mobileOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pendingKnowledge, setPendingKnowledge] = useState(0);

  useEffect(() => {
    const load = () => getBotKnowledgePendingCount().then((r) => setPendingKnowledge(r.data.pending || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await authLogout();
    } catch { /* logout cleanup */ }
    toast.success("Cikis yapildi");
    navigate('/login');
  };

  const handlePasswordChange = async () => {
    if (!pwForm.current_password || !pwForm.new_password) {
      toast.error("Tum alanlar gerekli");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Yeni sifreler uyusmuyor");
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error("Sifre en az 8 karakter olmali");
      return;
    }
    setPwLoading(true);
    try {
      const res = await changePassword({
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      });
      if (res.data?.token) localStorage.setItem("token", res.data.token);
      toast.success("Sifre degistirildi. Diger cihazlardaki oturumlar kapatildi.");
      setShowPasswordDialog(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sifre degistirme hatasi");
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <>
    <aside className={`w-[260px] h-screen bg-white border-r border-slate-200 flex flex-col fixed left-0 top-0 z-50 transition-transform duration-300 ${
      mobileOpen ? 'translate-x-0' : '-translate-x-full'
    } lg:translate-x-0`} data-testid="sidebar">
      {/* Logo */}
      <div className="h-16 flex items-center justify-between gap-3 px-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-red-600 rounded-lg flex items-center justify-center shrink-0">
            <span className="text-white font-barlow font-bold text-lg">U</span>
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-barlow font-bold text-slate-900 tracking-tight truncate">UcarFlow</h1>
            <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">CRM Sistemi</p>
          </div>
        </div>
        {onClose && <button onClick={onClose} className="lg:hidden p-1 rounded hover:bg-slate-100 text-slate-400">X</button>}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-red-50 text-red-700 border border-red-100'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent'
              }`
            }
            onClick={onClose}
          >
            <item.icon className="w-[18px] h-[18px] shrink-0" />
            <span className="flex-1">{item.label}</span>
            {item.to === "/bot-settings" && pendingKnowledge > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center" title={`${pendingKnowledge} bilgi onay bekliyor`} data-testid="nav-bot-pending-badge">{pendingKnowledge}</span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-slate-200 p-3">
        {user && (
          <div className="flex items-center gap-3 px-2 py-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-bold text-sm">
                {user.name?.[0] || 'U'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 truncate">{user.name || 'Kullanici'}</p>
              <p className="text-xs text-slate-400 truncate">{user.email || ''}</p>
            </div>
          </div>
        )}
        <button
          onClick={() => setShowPasswordDialog(true)}
          data-testid="change-password-btn"
          className="flex items-center gap-3 w-full px-3 py-2.5 mt-1 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
        >
          <KeyRound className="w-[18px] h-[18px]" />
          <span>Sifre Degistir</span>
        </button>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 w-full px-3 py-2.5 mt-1 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" />
          <span>Cikis Yap</span>
        </button>
      </div>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow">Sifre Degistir</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Mevcut Sifre</Label>
              <Input
                data-testid="current-password-input"
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Yeni Sifre</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Yeni Sifre (Tekrar)</Label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Iptal</Button>
            <Button onClick={handlePasswordChange} disabled={pwLoading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="password-submit-btn">
              {pwLoading ? "Kaydediliyor..." : "Degistir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
    </>
  );
}
